import { createContext, useCallback, useEffect, useState } from "react";
import { ApartmentComplex, ComplexFloor, ComplexParkingSpace, CreateApartmentComplex, CreateApartmentForComplex, CreateApartmentService, CreateSingleApartment, CreateSpaceParking, UploadApartmentFile, UploadComplexFile } from "../types/propertyInventory/ApartmentComplex";
import { propertyInventoryService } from "../services/propertyInventoryService";
import { CreateTenant, DocumentType } from "../types/propertyInventory/Tenant";
import { CreateRentPayment } from "../types/propertyInventory/RentPayment";
import { ServiceType } from "../types/propertyInventory/ServiceType";
import { ApartmentDto, RemoveTenantFromApartment } from "../types/propertyInventory/Apartment";

type PropertyInventoryContextType = {
    apartmentComplexes: ApartmentComplex[];
    apartmentComplex: CreateApartmentComplex;
    selectedApartmentComplexId?: string;
    complexFiles: File[];
    apartmentFiles: File[];
    apartments: CreateApartmentForComplex[];
    apartmentForComplex: CreateApartmentForComplex;
    setApartmentForComplex: React.Dispatch<React.SetStateAction<CreateApartmentForComplex>>;
    apartmentsByComplex: ApartmentDto[];
    services: CreateApartmentService[];
    tenants: CreateTenant[];
    parkingSpaces: CreateSpaceParking[];
    complexFloors: ComplexFloor[];
    complex: ApartmentComplex | null;
    getApartmentComplexById: (complexId: string) => Promise<ApartmentComplex | null>;
    getApartmentById: (apartmentId: string) => Promise<ApartmentDto | null>;
    apartmentById: ApartmentDto | null;
    complexParkingSpaces: ComplexParkingSpace[];
    servicesTypes: ServiceType[];
    setApartmentComplex: React.Dispatch<React.SetStateAction<CreateApartmentComplex>>;
    setComplexFiles: React.Dispatch<React.SetStateAction<File[]>>;
    setApartmentFiles: React.Dispatch<React.SetStateAction<File[]>>;
    setApartments: React.Dispatch<React.SetStateAction<CreateApartmentForComplex[]>>;
    setServices: React.Dispatch<React.SetStateAction<CreateApartmentService[]>>;
    setTenants: React.Dispatch<React.SetStateAction<CreateTenant[]>>;
    setParkingSpaces: React.Dispatch<React.SetStateAction<CreateSpaceParking[]>>;
    isLoading: boolean;
    isCreating: boolean;
    errorMessage: string | null;
    createApartmentComplex: (data: CreateApartmentComplex) => Promise<ApartmentComplex | null>;
    createSingleApartment: (data: CreateSingleApartment) => Promise<any>;
    getApartmentComplexesByOwnerId: (ownerId: number) => Promise<void>;
    getApartmentsByComplexId: (complexId: string) => Promise<any>;
    createApartmentForComplex: (data: CreateApartmentForComplex) => Promise<any>;
    createApartmentService: (data: CreateApartmentService) => Promise<any>;
    createTenant: (data: CreateTenant) => Promise<any>;
    createParkingSpace: (data: CreateSpaceParking) => Promise<any>;
    createRentPayment: (data: CreateRentPayment) => Promise<any>;
    uploadComplexFile: (data: UploadComplexFile) => Promise<any>;
    uploadApartmentFile: (data: UploadApartmentFile) => Promise<any>;
    getAllServicesTypes: () => Promise<any>;
    getParkingSpacesByComplexId: (complexId: string) => Promise<any>;
    getFloorsByComplexId: (complexId: string) => Promise<any>;
    removeTenantFromApartment: (data: RemoveTenantFromApartment) => Promise<any>;
    currentStep: number;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    selectedApartments: number;
    setSelectedApartments: React.Dispatch<React.SetStateAction<number>>;
    totalApartments: number;
    setTotalApartments: React.Dispatch<React.SetStateAction<number>>;
} | undefined;

const PropertyInventoryContext = createContext<PropertyInventoryContextType>(undefined);

const PropertyInventoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [apartmentComplexes, setApartmentComplexes] = useState<ApartmentComplex[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [apartmentFiles, setApartmentFiles] = useState<File[]>([]);
    const [complexFiles, setComplexFiles] = useState<File[]>([]);
    const [apartmentsByComplex, setApartmentsByComplex] = useState<ApartmentDto[]>([]);
    const [servicesTypes, setServicesTypes] = useState<ServiceType[]>([]);
    const [complexFloors, setComplexFloors] = useState<ComplexFloor[]>([]);
    const [complexParkingSpaces, setComplexParkingSpaces] = useState<ComplexParkingSpace[]>([]);
    const [complex, setComplex] = useState<ApartmentComplex | null>(null);
    const [selectedApartments, setSelectedApartments] = useState(0);
    const [totalApartments, setTotalApartments] = useState(0);
    const [apartmentById, setApartmentById] = useState<ApartmentDto | null>(null);

    const getApartmentById = useCallback(async (apartmentId: string): Promise<ApartmentDto | null> => {
        setIsLoading(true);
        const response = await propertyInventoryService.getApartmentById(apartmentId);
        if (response?.success) {
            setApartmentById(response.data);
            setErrorMessage(null);
        } else {
            setErrorMessage(response?.message || 'Failed to fetch apartment.');
        }
        setIsLoading(false);
        return response?.data || null;
    }, []);

    const [selectedApartmentComplexId, setSelectedApartmentComplexId] = useState<string>(
        () => {
            const stored = localStorage.getItem("selectedApartmentComplexId");
            return stored ? stored : "";
        }
    );
    const [apartmentComplex, setApartmentComplex] = useState<CreateApartmentComplex>(
        () => {
            const stored = localStorage.getItem("createApartmentComplex");
            return stored ? JSON.parse(stored) :
                {
                    name: "",
                    typeOfComplex: undefined,
                    ownerId: 0,
                    squareMeters: undefined,
                    totalFloors: undefined,
                    totalParkingSpaces: undefined,
                    description: "",
                    facebook: null,
                    instagram: null,
                    hasSecurity: false,
                    hasPool: false,
                    hasGym: false,
                    location: "",
                    files: [],
                }
        }
    );
    const [apartments, setApartments] = useState<CreateApartmentForComplex[]>([]);
    const [apartmentForComplex, setApartmentForComplex] = useState<CreateApartmentForComplex>(
        {
            apartmentNumber: "",
            apartmentComplexId: undefined,
            assignedFloorId: "",
            assignedParkingSpaceId: undefined,
            apartmentType: undefined,
            apartmentStatus: undefined as any,
            bathrooms: 0,
            squareMeters: 0,
            hasBalcony: false,
            baseRentAmount: 0,
            currency: undefined as any,
            securityDeposit: 0,
            description: "",
            tenant: {
            firstName: '',
            lastName: '',
            email: '',
            primaryPhone: '',
            secondaryPhone: '',
            profession: '',
            moveInDate: new Date().toISOString().split("T")[0],
            employer: '',
            monthlyIncome: 0,
            apartmentId: 'f3df35ef-16af-4cb6-bfdf-5c6c1f9078cf',
            documentNumber: '',
            documentType: DocumentType.CEDULA_NACIONAL,
            },
            apartmentHistory: {} as any,
            hasOwnParking: false,
            files: [],
            apartmentServices: [],
        },
    );
    const [services, setServices] = useState<CreateApartmentService[]>([]);
    const [tenants, setTenants] = useState<CreateTenant[]>([]);
    const [parkingSpaces, setParkingSpaces] = useState<CreateSpaceParking[]>([]);
    const [currentStep, setCurrentStep] = useState(0);


    useEffect(() => {
        if (apartmentComplex) {
            localStorage.setItem("createApartmentComplex", JSON.stringify(apartmentComplex));
        }
    }, [apartmentComplex]);

    useEffect(() => {
        const storedComplexId = localStorage.getItem("selectedApartmentComplexId");
        if (storedComplexId) {
            setSelectedApartmentComplexId(storedComplexId);
        }
    }, []);

    const createApartmentComplex = async (data: CreateApartmentComplex) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createApartmentComplex(data);
        if (response?.success) {
            setApartmentComplexes(response.data)
            setErrorMessage(null);
            setIsCreating(false);
            setSelectedApartmentComplexId(response.data.complexId);
            localStorage.setItem("selectedApartmentComplexId", response.data.complexId);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to create apartment complex.');
            setIsCreating(false);
            return null;
        }
    };

    const createSingleApartment = async (data: CreateSingleApartment) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createSingleApartment(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to create apartment complex.');
            setIsCreating(false);
            return null;
        }
    };

    const createApartmentForComplex = async (data: CreateApartmentForComplex) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createApartmentForComplex(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to create apartment complex.');
            setIsCreating(false);
            return null;
        }
    };

    const createApartmentService = async (data: CreateApartmentService) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createApartmentService(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to create apartment service.');
            setIsCreating(false);
            return null;
        }
    };

    const createTenant = async (data: CreateTenant) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createTenant(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        }
        else {
            setErrorMessage(response?.message || 'Failed to create tenant.');
            setIsCreating(false);
            return null;
        }
    };

    const createParkingSpace = async (data: CreateSpaceParking) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createParkingSpace(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to create parking space.');
            setIsCreating(false);
            return null;
        }
    };

    const createRentPayment = async (data: CreateRentPayment) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createRentPayment(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to create rent payment.');
            setIsCreating(false);
            return null;
        }
    };

    const getApartmentComplexesByOwnerId = useCallback(async (ownerId: number) => {
    setIsLoading(true);
    const response = await propertyInventoryService.getApartmentComplexesByOwnerId(ownerId);
    if (response?.success) {
        setApartmentComplexes(response.data);
        setErrorMessage(null);
    } else {
        setErrorMessage(response?.message || 'Failed to fetch apartment complexes.');
    }
    setIsLoading(false);
}, []);

    const getApartmentsByComplexId = useCallback(async (complexId: string) => {
        setIsLoading(true);
        const response = await propertyInventoryService.getApartmentsByComplexId(complexId);
        if (response?.success) {
            setErrorMessage(null);
            setIsLoading(false);
            setApartmentsByComplex(response.data);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to fetch apartments.');
            setIsLoading(false);
            setApartmentsByComplex([]);
            return [];
        }
    }, []);

    const uploadComplexFile = async (data: UploadComplexFile) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createUploadComplexFiles(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to upload complex file.');
            setIsCreating(false);
            return null;
        }
    };

    const uploadApartmentFile = async (data: UploadApartmentFile) => {
        setIsCreating(true);
        const response = await propertyInventoryService.createUploadApartmentFiles(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to upload apartment file.');
            setIsCreating(false);
            return null;
        }
    };

    const getAllServicesTypes = useCallback(async () => {
        setIsLoading(true);
        const response = await propertyInventoryService.getAllServicesTypes();
        if (response?.success) {
            setServicesTypes(response.data);
            setErrorMessage(null);
        } else {
            setErrorMessage(response?.message || 'Failed to fetch services.');
        }
        setIsLoading(false);
    }, []);

    const getParkingSpacesByComplexId = useCallback(async (complexId: string) => {
        setIsLoading(true);
        const response = await propertyInventoryService.getParkingSpacesByComplexId(complexId);
        if (response?.success) {
            setErrorMessage(null);
            setIsLoading(false);
            setComplexParkingSpaces(response.data);
            return response.data;
        }
        else {
            setErrorMessage(response?.message || 'Failed to fetch parking spaces.');
            setIsLoading(false);
            return [];
        }
    }, []);

    const getFloorsByComplexId = useCallback(async (complexId: string) => {
        setIsLoading(true);
        const response = await propertyInventoryService.getFloorsByComplexId(complexId);
        if (response?.success) {
            setErrorMessage(null);
            setIsLoading(false);
            setComplexFloors(response.data);
            return response.data;
        }
        else {
            setErrorMessage(response?.message || 'Failed to fetch floors.');
            setIsLoading(false);
            return [];
        }
    }, []);

    const getApartmentComplexById = useCallback(async (complexId: string): Promise<ApartmentComplex | null> => {
        setIsLoading(true);
        const response = await propertyInventoryService.getApartmentComplexById(complexId);
        if (response?.success) {
            setErrorMessage(null);
            setIsLoading(false);
            setComplex(response.data);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to fetch apartment complex.');
            setIsLoading(false);
            setComplex(null);
            return null;
        }
    }, []);

    const removeTenantFromApartment = async (data: RemoveTenantFromApartment) => {
        setIsCreating(true);
        const response = await propertyInventoryService.removeTenantFromApartment(data);
        if (response?.success) {
            setErrorMessage(null);
            setIsCreating(false);
            return response.data;
        } else {
            setErrorMessage(response?.message || 'Failed to remove tenant from apartment.');
            setIsCreating(false);
            return null;
        }
    };

    return (
        <PropertyInventoryContext.Provider value={{
            apartmentComplexes,
            isLoading,
            isCreating,
            errorMessage,
            apartmentComplex,
            complexFiles,
            apartmentFiles,
            apartments,
            services,
            tenants,
            apartmentForComplex,
            setApartmentForComplex,
            apartmentsByComplex,
            parkingSpaces,
            servicesTypes,
            complexFloors,
            complexParkingSpaces,
            selectedApartmentComplexId,
            complex,
            selectedApartments,
            setSelectedApartments,
            totalApartments,
            setTotalApartments,
            getApartmentComplexById,
            setApartmentComplex,
            setComplexFiles,
            setApartmentFiles,
            setApartments,
            setServices,
            setTenants,
            setParkingSpaces,
            getAllServicesTypes,
            getParkingSpacesByComplexId,
            getFloorsByComplexId,

            createApartmentComplex,
            createSingleApartment,
            getApartmentComplexesByOwnerId,
            createApartmentForComplex,
            createApartmentService,
            createTenant,
            createParkingSpace,
            createRentPayment,
            getApartmentsByComplexId,
            uploadComplexFile,
            uploadApartmentFile,
            getApartmentById,
            removeTenantFromApartment,
            apartmentById,

            currentStep,
            setCurrentStep,
        }}>
            {children}
        </PropertyInventoryContext.Provider>
    );
};

export { PropertyInventoryContext, PropertyInventoryProvider };